import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// The site can be served from both "domain.com" and "www.domain.com" (one may redirect to the other).
// Browsers can't follow redirects on cross-origin form submissions, so call the API on the host the visitor is on.
const bare = (h) => h.replace(/^www\./, "");
const onSameSite = typeof window !== "undefined" && bare(new URL(BACKEND).host) === bare(window.location.host);
export const API = `${onSameSite ? window.location.origin : BACKEND}/api`;
export const http = axios.create({ baseURL: API, withCredentials: true });

export const DIVISIONS = [
  {
    key: "truck", name: "Truck Division", short: "Truck", img: "/img/truck.jpg", usage: "Mileage",
    blurb: "Sleeper and day cab semi-trucks, heavy- and medium-duty commercial trucks, box trucks, dump trucks, tractor units and specialty commercial trucks.",
    categories: ["Sleeper Cab Semi-Trucks", "Day Cab Semi-Trucks", "Heavy-Duty Commercial Trucks", "Medium-Duty Trucks", "Box Trucks", "Dump Trucks", "Tractor Units", "Specialty Commercial Trucks"],
  },
  {
    key: "trailer", name: "Trailer Division", short: "Trailer", img: "/img/trailer.jpg", usage: "Mileage",
    blurb: "Dry vans, reefers, flatbeds, step decks, lowboys, tankers, dump trailers and specialty or heavy-haul trailers.",
    categories: ["Dry Van Trailers", "Refrigerated Trailers (Reefers)", "Flatbed Trailers", "Step Deck Trailers", "Lowboy / Lowbed Trailers", "Tanker Trailers", "Dump Trailers", "Specialty and Heavy-Haul Trailers"],
  },
  {
    key: "marine", name: "Marine Division", short: "Marine", img: "/img/marine.jpg", usage: "Engine hours",
    blurb: "Boats and yachts only: motor and luxury yachts, motorboats, fishing and sport boats, cabin cruisers, pontoons and commercial marine vessels.",
    categories: ["Motor Yachts", "Luxury Yachts", "Motorboats", "Fishing Boats", "Sport Boats", "Cabin Cruisers", "Pontoon Boats", "Commercial Marine Vessels"],
  },
  {
    key: "equipment", name: "Equipment Division", short: "Equipment", img: "/img/equipment.jpg", usage: "Operating hours",
    blurb: "Excavators, wheel loaders, bulldozers, backhoes, skid steers, telehandlers, cranes, forklifts, industrial machinery and road construction equipment.",
    categories: ["Excavators", "Wheel Loaders", "Bulldozers", "Backhoe Loaders", "Skid Steer Loaders", "Telehandlers", "Cranes", "Forklifts", "Industrial Machinery", "Road Construction Equipment"],
  },
];
export const divisionOf = (key) => DIVISIONS.find((d) => d.key === key);

export const STATUSES = {
  available: { label: "Available", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
  reserved: { label: "Reserved", cls: "text-rose-300 border-rose-400/40 bg-rose-400/10" },
  pending: { label: "Pending", cls: "text-orange-300 border-orange-400/40 bg-orange-400/10" },
  being_sourced: { label: "Being Sourced", cls: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
  unavailable: { label: "Unavailable", cls: "text-slate-300 border-slate-400/40 bg-slate-400/10" },
  sold: { label: "Sold", cls: "text-slate-300 border-slate-400/40 bg-slate-400/10" },
};

export const SOURCES = {
  company_owned: "Owned by Selby Asset Group",
  third_party: "Listed through a third-party seller",
  sourced_for_client: "Sourced for a client",
  unspecified: "Ownership not specified",
};

export const CONDITIONS = ["New", "Like New", "Excellent", "Good", "Fair", "Used", "For Parts / Project"];

export const DEFAULT_SETTINGS = {
  company_name: "Selby Asset Group",
  owner_name: "Scott W. Selby",
  owner_email: "scottwselby@selbyassetgroup.com",
  manager_name: "Thomas Abraham",
  manager_email: "thomasabraham@selbyassetgroup.com",
  info_email: "info@selbyassetgroup.com",
  sales_email: "sales@selbyassetgroup.com",
  address_line1: "39 Samuel Dr",
  address_line2: "Streamwood, IL 60107",
  phone: "224-255-2561",
  hero_eyebrow: "Asset Sourcing & Brokerage",
  hero_title: "Commercial assets,\nsourced with\nintegrity.",
  hero_subtitle: "Selby Asset Group helps buyers find trucks, trailers, marine vessels and heavy equipment through relevant channels and trusted business relationships, with clear, honest communication at every step.",
  about_body: "",
  mission: "To connect customers with suitable assets through professional sourcing, reliable communication, and transparent business relationships.",
  sourcing_message: "Looking for a specific asset? Selby Asset Group works on a sourcing-request basis, helping customers identify suitable assets through relevant channels and business relationships.",
  reviews_placeholder: "Client testimonials will be featured here as customers share their experiences with Selby Asset Group.",
};

export const imageUrl = (p) => (!p ? "" : p.startsWith("http") || p.startsWith("/") ? p : `${API}/files/${p}`);

export const formatPrice = (p) =>
  p === null || p === undefined || p === "" ? "Request Price" : `$${Number(p).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};
export const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "");

export const telHref = (p) => `tel:${(p || "").replace(/[^0-9+]/g, "")}`;

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && typeof x.msg === "string" ? x.msg.replace("Value error, ", "") : JSON.stringify(x))).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
}
